/**
 * THE REST OF THE AIRTABLE COLUMNS, READ OFF AN ECH DOCUMENT.
 *
 * `visualSpec` reads its per-block columns through `first("listing")`, `first("map")`,
 * `first("amenities")`, `first("isc")`, `first("logo")`, `first("footer")` — all REPO
 * types. Every block in an ECH document is typed `sfmc_<contentId>`, so none of those
 * can ever match and the columns ship EMPTY on every ECH build.
 *
 * Four of these were bridged one at a time as each was reported — images
 * (`echImagesOf`), buttons (`echCtasOf`), modules (`echModuleOption`), copy
 * (`echTextOf`), and the invite's event details (`echEventOf`). This is the same bug in
 * the ~12 columns nobody had reported yet. MEASURED 2026-08-27 on the parity sweep's own
 * row `recCpENF5L2Y3spHm` ("TEST Available Now 8/27"): Listing Table, Amenities, Contact
 * Info, Map Address/Input, Logo Header, Disclaimer and Main Image Info all blank on a
 * build whose canvas showed every one of them. The sweep read "Module 1 = Listing Table"
 * as the listing landing — that is the module NAME out of `echModuleOption`, not the
 * rows.
 *
 * SAME DISCIPLINE AS THE REST OF THE BRIDGE:
 *   · an EXPLICIT block→field map, never a heuristic. Picking fields by name or type
 *     would sweep up button labels, image alts, amenity captions and section banners.
 *   · every key below was read off the LIVE block guardrails (`/sfmc-blocks?op=list
 *     &deep=1`, 36 blocks) — not inferred. A block that is not listed contributes
 *     nothing.
 *   · a column the ECH blocks genuinely cannot answer is LEFT ALONE rather than filled
 *     with something adjacent. Deliberately absent: Floorplan Module, Checkboard Module
 *     and Featured Text (no ECH block exists for any of them), and Disclaimer — the
 *     `ECH_Disclaimer` family declares only a `ContactUrl`, no disclaimer copy, so there
 *     is nothing to map and borrowing the footer's text would put the wrong words in a
 *     legal column. Map Module is not inferred either: it is a single-select and the
 *     block carries no community/market distinction to read.
 */
import { echNameFor, type EchAware, echImagesOf, echIsHero } from "./echSpecBridge";
import { echMeta } from "./echRegistry";
import { bynderAssetName, bynderMediaLink } from "./bynderCrop";
import { heroGifSpecOf, type HeroGifSpec } from "./heroGif";
import { htmlRowNumbers, rowCollections, type RowCollection } from "./echRows";
import { overlayInfoLines } from "./heroOverlay";

type Data = Record<string, unknown>;
type Row = Record<string, unknown>;

const S = (v: unknown): string => (v == null ? "" : String(v));
const T = (v: unknown): string => S(v).trim();
/** join the non-empty parts, one per line — the shape visualSpec uses everywhere */
const lines = (...a: unknown[]): string => a.map(S).filter((x) => x.trim() !== "").join("\n");
const dataOf = (b: EchAware): Data => (b && b.data) || {};

/* ── which blocks answer which column ──────────────────────────────────────────── */
const AMENITY_BLOCKS = new Set(["ech_amenities", "ech_4amenities", "ech_3amenities"]);
const FEATURED_BLOCKS = new Set(["ech_featured1cta", "ech_featured2cta"]);
/** the same five hero blocks `echIsHero` knows, for the Main Image columns */
const HERO_BLOCKS = new Set([
  "ech_mainimage", "ech_mainimage600", "ech_comingsoonimage", "ech_availablenowimage",
  "ech_justlaunchedimage", "ech_finddreamhomeimage", "ech_vipimage",
]);

/* exported because `visualSpec` has to know this block already has a NAMED list of its
   own (`Amenities Images`, built below) and must not ALSO report its icons in the
   generic spillover — see the dedupe note in visualSpec's image loop (2026-08-29) */
export const echIsAmenity = (b: EchAware) => AMENITY_BLOCKS.has(echNameFor(b).toLowerCase());
const isAmenity = echIsAmenity;
export const echIsFeatured = (b: EchAware) => FEATURED_BLOCKS.has(echNameFor(b).toLowerCase());
/* one answer with echSpecBridge's `echIsHero`: the listed names, plus any headline banner
   by its guardrail module (2026-10-06 — the three new templates' banners) */
const isHero = (b: EchAware) => HERO_BLOCKS.has(echNameFor(b).toLowerCase()) || echIsHero(b);

/**
 * ONE LISTING ROW PER LINE, grouped under the collection it sits under IN THE EMAIL.
 *
 * A row counts as present when it names a HOMESITE or a PLAN — the same test the
 * renderer and the required-gate use, so a row this reports is a row that ships. Each
 * collection name is emitted as a heading so creative can see which homes belong to
 * which collection.
 *
 * ── WHICH HOMES A COLLECTION OWNS IS READ OFF THE MARKUP (COS 70141, Justin Bradford,
 * 2026-10-13) ──────────────────────────────────────────────────────────────────────
 * This used to hard-code Layne's original two-section layout: homes 1–5 under
 * `collection1Name`, 6–10 under `collection2Name`. The fill re-cuts the table into as
 * many sections as there are communities (`echExpand`, since 2026-08-22), so a
 * five-section table reached Airtable as two headings with the homes split 4 / 3 under
 * the wrong names, and collections 3–5 never appeared. Ownership now comes from
 * `rowCollections(__html)` — the same parse the panel uses — so the column, the panel
 * and the canvas agree. Added collections (the `<!--CN-->` literal) are named too.
 *
 * A collection with no filled home is left out, matching the render, which no longer
 * draws a heading over nothing (`dropEmptyCollectionHeaders`).
 */
const unEsc = (s: string) => s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

function listingRowText(d: Data, n: number): string {
  const hs = T(d["home" + n + "Homesite"]);
  const pl = T(d["home" + n + "Plan"]);
  if (!hs && !pl) return "";
  const specs = ["SqFt", "Beds", "Baths", "Price"]
    .map((k) => T(d["home" + n + k]))
    .filter(Boolean)
    .join(" | ");
  const url = T(d["home" + n + "Url"]);
  return lines(
    [hs, pl].filter(Boolean).join(" — "),
    specs ? "   " + specs : "",
    url ? "   " + url : "",
  );
}

function listingRows(b: EchAware): string {
  const d = dataOf(b);
  const html = T(d.__html);
  /* no markup on the block (a cold instance, a unit fixture) → the block's own default
     layout, which is the two-section 1–5 / 6–10 cut */
  const groups: RowCollection[] = html
    ? rowCollections(html, htmlRowNumbers(html).length ? htmlRowNumbers(html) : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
    : [
        { key: "collection1Name", homes: [1, 2, 3, 4, 5] },
        { key: "collection2Name", homes: [6, 7, 8, 9, 10] },
      ];
  const out: string[] = [];
  for (const g of groups) {
    const rows = g.homes.map((n) => listingRowText(d, n)).filter(Boolean);
    if (!rows.length) continue;
    const head = g.key ? T(d[g.key]) : g.lit != null ? T(unEsc(g.lit)) : "";
    if (head) out.push(out.length ? "\n" + head : head);
    out.push(...rows);
  }
  return out.join("\n");
}

/**
 * IS THIS VALUE STILL THE ONE THE TEMPLATE SHIPPED WITH?
 *
 * MEASURED 2026-08-27 on row `rec4Ocv1eC6Zd7394`: the Amenities column landed as EIGHT
 * lines reading the single word "Amenity", because `ECH_Amenities` declares
 * `label1..8` with `default: "Amenity"` and an untouched block hands those defaults
 * straight through. That is WORSE than an empty column — Creative cannot tell an
 * untouched Amenities bar from a real amenity list, so it has to go and ask.
 *
 * The declared default is the honest test and it comes from the block's own guardrail
 * (`echMeta(type).declared[key].default`), never a word list in this repo: Layne can
 * rename a placeholder and this keeps working. `echRequired` adopted the same rule for
 * images in #47 — "a photo that renders is not a missing photo" is the same idea read
 * from the other side.
 *
 * A block with no registered meta (the registry is only populated from a live SFMC
 * pull) falls back to the literal placeholder words, so a unit test and a cold start
 * both still skip the obvious ones.
 */
const PLACEHOLDER = /^(amenity|amenities|community name|city, ?state|headline|subheadline|body copy|button|label)$/i;

const isDefault = (b: EchAware, key: string, value: string): boolean => {
  const v = T(value);
  if (!v) return true;
  const meta = echMeta(String((b as { type?: string }).type || ""));
  const dec = meta && meta.declared && meta.declared[key];
  if (dec && typeof dec.default === "string" && T(dec.default) !== "") {
    return T(dec.default) === v;
  }
  return PLACEHOLDER.test(v);
};

/** the field's value, or "" when it is still the template's placeholder */
const own = (b: EchAware, key: string): string => {
  const v = T(dataOf(b)[key]);
  return isDefault(b, key, v) ? "" : v;
};

/** the amenity captions the requester picked, one per line (icons live in the images) */
function amenityLabels(b: EchAware): string {
  const out: string[] = [];
  for (let n = 1; n <= 8; n++) {
    /* `own` drops a caption still reading the declared placeholder ("Amenity") — an
       untouched bar must contribute NOTHING rather than eight identical rows. */
    const v = own(b, "label" + n);
    if (v) out.push(v);
  }
  return out.join("\n");
}

/**
 * ── ONE LINE PER IMAGE, UNCAPPED (2026-08-28, Katy) ───────────────────────────
 *
 * Katy: "every image and link should map to a specific named key for its block…
 * Right now anything without a dedicated key falls into a positional bucket, and
 * position shifts with block order, so the same slot means different things on
 * different emails. That's not something I can map against reliably." And on why a
 * list rather than more numbered keys: "A capped set of numbered keys drops anything
 * past the cap with no error. A list just gets longer."
 *
 * That is exactly what was happening. The named image slots cap at three per role
 * (`SLOT_CAPS`, visualSpec.ts), so a 4-amenity block exhausted the three Other Block
 * Image slots and the fourth icon was DISCARDED — silently, mid-`visualSpec`. The
 * captions still landed in `Amenities`, so the column looked complete while the icon
 * URLs had gone.
 *
 * Shape follows the two conventions already in this file: newline-delimited like
 * `Amenities`, and the em-dash separator of `Featured Plan/Community Module`
 * ("Plan 1 — Name"). NEVER a "N. " prefix — Airtable richText renders that as an
 * ordered list and renumbers every line (visualSpec.ts:374).
 *
 * Pairs POSITIONALLY with its sibling list, which is what makes it mappable: line 3
 * of `Amenities Images` is the icon for line 3 of `Amenities`.
 */
function imageList(b: EchAware, nameFor: (im: { key: string; url: string; alt: string }, i: number) => string): string {
  return echImagesOf(b)
    .filter((im) => T(im.url))
    .map((im, i) => {
      const nm = T(nameFor(im, i));
      return nm ? nm + " \u2014 " + T(im.url) : T(im.url);
    })
    .join("\n");
}

/**
 * What `Main Image Info` says for a hero photo.
 *
 * ── THE OPS TEAM WANTS THE ASSET, NOT THE RENDER (2026-09-15, Justin) ──────────────
 * The column carried the transform URL the email ships
 * (`…/transform/<uuid>/<name>?io=transform:fill,width:500,height:350`). That is the right
 * thing for the email and useless to the person building it: it opens a 500×350 JPEG, not
 * the asset, so they searched Bynder by hand for the name in the path. Justin asked for the
 * DAM link instead — `…/media/?field=text&value=<name>…&mediaId=<id>` — which lands on the
 * asset itself.
 *
 * Two lines, the asset first because that is the ask:
 *   <AssetName> — <DAM link>
 *   As placed — <the exact transform URL>
 * The second line stays because it is the only record of the crop the requester chose —
 * since #zoom+drag the URL carries the view (bynderCrop.ts), and dropping it would send
 * ops the whole photo with no idea how it was framed. Same "name — url" shape as every
 * other image column here, newline-delimited, never a "N. " prefix (richText renumbers).
 *
 * Anything that is not a Bynder transform URL (a template default on the email CDN, a
 * locked launch banner on stensul) has no DAM page and is written as before: the bare URL.
 */
export function mainImageInfo(url: string, gif?: HeroGifSpec | null): string {
  const u = T(url);
  if (gif && gif.imgs.length) return gifImageInfo(u, gif);
  const dam = bynderMediaLink(u);
  if (!dam) return u;
  return (bynderAssetName(u) || "Bynder asset") + " — " + dam + "\nAs placed — " + u;
}

/**
 * ── THE HERO IS AN ANIMATED GIF (2026-09-16) ─────────────────────────────────────────
 * The Studio builds the GIF from 2–5 Bynder photos and the submit hosts it in Content
 * Builder, so the URL the email ships is SFMC's, not Bynder's — and on its own it tells
 * ops nothing about where the photos came from or how each was framed. So the column
 * says what it is, then lists every source photo in the same "<Asset> — <DAM link> /
 * As placed — <url>" shape the single-photo hero uses. "Photo N" rather than "N." — a
 * richText column renumbers a leading "N. ".
 *   Animated GIF · 3 photos · 1.5s each — <hosted GIF url>
 *   Photo 1 · <Asset> — <DAM link>
 *   As placed — <frame url with its crop>
 *   …
 * A GIF that has not been hosted yet (a data: URL — the submit stops before it ships,
 * hostImages.ts) is named as such rather than pasted as a megabyte of base64.
 */
export function gifImageInfo(url: string, gif: HeroGifSpec): string {
  const u = T(url);
  const head = "Animated GIF · " + gif.imgs.length + " photos · " + (Math.round(gif.delay / 100) / 10) + "s each — " + (/^data:/i.test(u) ? "(not hosted yet)" : u);
  const lines = gif.imgs.map((f, i) => {
    const fu = T(f);
    const dam = bynderMediaLink(fu);
    return "Photo " + (i + 1) + " · " + (dam ? (bynderAssetName(fu) || "Bynder asset") + " — " + dam + "\nAs placed — " + fu : fu);
  });
  return [head, ...lines].join("\n");
}

/**
 * A featured block as the Featured Plan/Community column reads.
 *
 * "Plan N — " rather than "N. " on purpose: Airtable richText turns "N. " into a markdown
 * ordered list and the indented sub-lines break it, so every card renumbered itself to 1
 * (visualSpec, same fix).
 */
function featuredCard(b: EchAware, i: number): string {
  const d = dataOf(b);
  /* Price rides along when the card carries one (2026-09-22, Justin on NEV 68689: "the
     price callout is missing" — the email read $737,569 under the specs, the column did
     not; `listingRows` next door has always included it) */
  /* the price's label is a PREFIX ("From $415,990"), the others are suffixes ("1,600 sq ft");
     no price means no prefix either (echPrice, 2026-10-26) */
  const price = T(d.Price) ? (T(d.PriceLabel) ? T(d.PriceLabel) + " " : "") + T(d.Price) : "";
  const specs = [
    [T(d.SqFt), T(d.SqFtLabel)], [T(d.Beds), T(d.BedsLabel)], [T(d.Baths), T(d.BathsLabel)],
  ].map(([v, l]) => (v ? (l ? v + " " + l : v) : "")).concat(price).filter(Boolean).join(" | ");
  /* a one-button block names it BtnLabel + FloorplanUrl, a two-button block uses the
     btn1Label/btn1Url and btn2Label/btn2Url pairs */
  const btns = [
    [T(d.BtnLabel) || "View Floorplan", T(d.FloorplanUrl)],
    [T(d.btn1Label), T(d.btn1Url)],
    [T(d.btn2Label), T(d.btn2Url)],
  ].filter(([, u]) => u && u !== "#");
  return lines(
    "Plan " + i + " — " + T(d.PlanName),
    specs ? "   " + specs : "",
    ...btns.map(([l, u]) => "   Button: " + (l || "View") + " → " + u),
  );
}

/**
 * The first REAL image url in a block's own markup — how a locked hero reports the photo
 * it ships (2026-08-27).
 *
 * `https?://` only, on purpose. A block WITH a declared `imgSrc` carries `{{imgSrc}}` in
 * its markup, and a token, a `data:` uri or a merge field is not a photo — reporting one
 * would put junk in the column, which is worse than the blank this replaces.
 */
/**
 * The buttons a Contact Us block ships, as `[label, href]` pairs in document order
 * (2026-09-22, see the Contact Info column).
 *
 * A destination is `mailto:`, `tel:` or `https?://` — a `{{Token}}`, a bare `#` or an
 * empty href is not a button anyone can press and is skipped. The label is the anchor's
 * visible text with tags stripped (an image-only anchor has none and falls back to
 * "Contact Us"). Duplicates by label+href collapse to one, so a block that repeats its
 * button for a mobile layout reports it once.
 */
/**
 * An attribute or text value as the reader sees it, not as the markup stores it
 * (2026-09-25). The block writes `&` in a link as `&amp;`, which is correct HTML, but
 * the Contact Info column is plain text: from 09-24 every Contact Us link reached
 * Airtable and Katy's Projects copy as `…?state=…&amp;market=…`. Only the entities a
 * link or label actually carries are decoded; anything else is left as written.
 */
export function unentity(v: string): string {
  return String(v || "")
    .replace(/&#(\d+);/g, (_w, n: string) => String.fromCharCode(+n))
    .replace(/&#x([0-9a-f]+);/gi, (_w, n: string) => String.fromCharCode(parseInt(n, 16)))
    .replace(/&quot;/gi, '"').replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&");
}

export function contactButtons(html: unknown): [string, string][] {
  const re = /<a\b[^>]*\bhref\s*=\s*("([^"]*)"|'([^']*)')[^>]*>([\s\S]*?)<\/a>/gi;
  const h = S(html);
  const out: [string, string][] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(h))) {
    const u = unentity(S(m[2] || m[3])).trim();
    if (!/^(mailto:|tel:|https?:\/\/)/i.test(u) || /\{\{/.test(u)) continue;
    const label = unentity(S(m[4]).replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ")).replace(/\s+/g, " ").trim() || "Contact Us";
    const key = label.toLowerCase() + "|" + u.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([label, u]);
  }
  return out;
}

export function firstImgSrc(html: unknown): string {
  const re = /<img\b[^>]*\bsrc\s*=\s*("([^"]*)"|'([^']*)')/gi;
  const h = S(html);
  let m: RegExpExecArray | null;
  while ((m = re.exec(h))) {
    const u = unentity(S(m[2] || m[3])).trim();
    if (/^https?:\/\//i.test(u)) return u;
  }
  return "";
}

/**
 * Flatten the ECH-only columns. Returns ONLY keys it can answer, so the caller can
 * merge it without ever blanking something a repo block already filled.
 */
export function echColumns(doc: EchAware[]): Row {
  const o: Row = {};
  const d = doc || [];
  
  const put = (k: string, v: unknown) => { const s = T(v); if (s) o[k] = s; };
  const firstNamed = (test: (b: EchAware) => boolean): EchAware | null =>
    d.find((b) => test(b)) || null;
  const named = (name: string) => firstNamed((b) => echNameFor(b).toLowerCase() === name);

  /* ── Listing ───────────────────────────────────────────────────────────────── */
  const listing = named("ech_listingtable");
  if (listing) put("Listing Table", listingRows(listing));
  /* the View All pair lives on the SECTION HEADER block, not the table (ECH_TableHeader
     declares linkLabel + linkUrl); ECH_BlueHeader is the same banner WITHOUT a link */
  const tableHead = named("ech_tableheader");
  if (tableHead) {
    put("Listing — View All Label", dataOf(tableHead).linkLabel);
    put("Listing — View All URL", dataOf(tableHead).linkUrl);
  }

  /* ── Amenities ─────────────────────────────────────────────────────────────── */
  const amen = firstNamed(isAmenity);
  if (amen) {
    put("Amenities", amenityLabels(amen));
    /* the icons, paired to those captions by position — `icon3` takes `label3`, read
       through `own` so an untouched placeholder caption contributes no name rather
       than eight identical ones */
    put("Amenities Images", imageList(amen, (im) => own(amen, im.key.replace(/^icon/i, "label"))));
  }

  /* ── Map / Get Directions ──────────────────────────────────────────────────── */
  const map = named("ech_getdirections");
  if (map) {
    const m = dataOf(map);
    /* THE MAP ITSELF RIDES THE SAME COLUMN (2026-10-27, Layne, Teams: Visit Us map fields on
       ECH_GetDirections are MapSrc (the image), MapAlt (its alt text) and MapUrl (its link);
       "over to you to add them to Map Address/Input"). Read through `own`, so the block's
       untouched placeholder map adds nothing. */
    const mapSrc = own(map, "MapSrc"), mapAlt = own(map, "MapAlt"), mapUrl = own(map, "MapUrl");
    put("Map Address/Input", lines(
      T(m.Community), T(m.AddressLine1), T(m.AddressLine2), T(m.Phone),
      T(m.DirectionsUrl) ? "Button: Get Directions → " + T(m.DirectionsUrl) : "",
      mapSrc ? "Map image: " + mapSrc : "",
      mapAlt ? "Map image alt: " + mapAlt : "",
      mapUrl ? "Map link: " + mapUrl : "",
    ));
  }

  /* ── Contact Us ────────────────────────────────────────────────────────────── */
  const isc = named("ech_contactusdefault");
  if (isc) {
    /* ── THE COLUMN REPORTS THE BUTTONS THE EMAIL SHIPS, NOT THE ONE FIELD (2026-09-22) ──
       Justin Bradford, NEV job 68604: "their contact module has two buttons, but only 1
       of the buttons information is pulling into Airtable." The block declares ONE field
       (`ContactUrl`), and this line used to print it under a hard-coded "Contact Us". But
       the buttons a division actually gets come from its `Division_Contact_Us_Info` row at
       render time — `echContactPatch` → `contactTreatmentHtml` writes them into the block's
       `__html` (NEV: `Email Us` on a mailto: AND `Call Us` on a tel:, the `IFCOLUMN:Cta2_Url`
       region Layne added on 09-11). So NEV's column dropped Call Us and mislabelled Email
       Us, and no other division noticed because their one button really is Contact Us.
       Read the anchors off the treated markup — the same way `firstImgSrc` reports a locked
       hero's photo — one `Button: label → href` per line, in the order the email shows
       them. The declared field is the fallback for a block the fill has not treated yet
       (its markup still carries `{{ContactUrl}}`, which is a token, not a destination). */
    const d = dataOf(isc);
    const btns = contactButtons(d.__html);
    if (btns.length) put("Contact Info", btns.map(([l, u]) => "Button: " + l + " → " + u).join("\n"));
    else {
      const u = T(d.ContactUrl);
      if (u) put("Contact Info", "Button: Contact Us → " + u);
    }
  }

  /* ── Logo header ───────────────────────────────────────────────────────────── */
  const logo = named("ech_logo");
  /* community, city/state AND phone — the three lines the header renders. cityState was
     missing, so a market-scope send (no community) reported a bare phone number while the
     email plainly read "Atlanta, GA | 770-670-2770". Placeholders are dropped, so an
     untouched header contributes nothing at all rather than "Community Name". */
  if (logo) {
    put("Logo Header", lines(own(logo, "community"), own(logo, "cityState"), own(logo, "phone")));
    /* ── AND THE COMMUNITY ON ITS OWN (2026-08-31, Katy: "let's break it out") ────
       The joined value is still sent — Creative reads it as the three header lines —
       but the community name is the part anyone downstream actually filters on, and
       it is not usefully greppable out of a three-line join. */
    put("Community Name", own(logo, "community"));
  }

  /* ── THE NAME UNDER THE MAIN IMAGE (2026-09-21, Justin on BOI 68501) ──────────────
     "The Community name under the main image is not pulling in correctly. The builder
     preview is showing Legacy at Heritage Heights and the Airtable spec sheet is showing
     just Heritage Heights." Both were what the requester typed — the header's community
     line read "Heritage Heights", the Community Subhead "Legacy at Heritage Heights" — and
     the spec's "Community Name Callout" is an intake formula over `Community Name`, i.e.
     the HEADER line above. The subhead itself was never in the payload at all
     (`ECH_TEXT_KEYS` predates the block), so ops had no way to see what the email says
     under the photo. It goes to `Subheadline`: the column exists on intake and in the
     BE allow-list, and it is literally the sub-headline under the hero. `own()` drops
     Layne's shipped placeholder ("Community Name") so an unfilled block adds nothing. */
  const sub = named("ech_communitysubhead");
  if (sub) put("Subheadline", own(sub, "subhead"));

  /* ── The section bar's own title ─────────────────────────────────────────────
     Katy, pointing at the banner above Amenities ("Canyons - The Valley at Wildfl…"):
     "it's not coming over under Logo Header that I'm seeing."

     It never was. That banner is ECH_BlueHeader (module `sectionBar`, one declared
     field `sectionTitle`) — a DIFFERENT block from the logo header at the top of the
     email, and its text was not emitted anywhere. The sibling ECH_TableHeader is the
     same banner WITH a link, and only its link pair was being sent (see "Listing —
     View All …" above), so its title was missing too. Both are covered here. */
  const bar = named("ech_blueheader") || named("ech_tableheader");
  /* ── THE EFFECTIVE TITLE, NOT THE REQUESTER-OWNED ONE (2026-09-02) ─────────────
     This used `own()`, which drops a value equal to the block's declared default — the
     right rule for a placeholder like "Amenity" in a caption slot, and the wrong rule
     here. `ECH_TableHeader.sectionTitle` is ECH_FIXED: template furniture that resolves
     from `table.title` to the block's AUTHORED default (or "Amenities" when the next
     block is the amenity grid), and the requester cannot type over it. So the effective
     value equals the default by construction, `own()` returned "" every time, and the
     column Katy asked for on 08-31 could never populate. Measured on the first real
     submission after both sides deployed (recUcaKn3BHUMs01W, 2026-09-02 13:10Z): Block
     Order carried "Table Header Bar", `Section Header` was empty.

     The unit tests passed because `blk()` does not register the block, so `isDefault`
     had no declared default to compare against and fell through to the placeholder
     regex. Same shape as the pattern-sweep trap: the function was right, the argument
     the production path handed it was not. The new test registers the block. */
  if (bar) put("Section Header", T(dataOf(bar).sectionTitle));

  /* ── Footer social links ───────────────────────────────────────────────────── */
  const soc = named("ech_socialicons");
  if (soc) {
    const s = dataOf(soc);
    /* An EMPTY value means that icon is OFF — an instruction that exists nowhere else
       in the payload, so all six lines are emitted (visualSpec's own contract).
       linkedin is always blank: the block declares no LinkedIn field, i.e. it is
       structurally off, which is exactly what an empty value says. */
    const pairs: [string, unknown][] = [
      ["facebook", s.facebookUrl], ["x", s.xUrl], ["instagram", s.instagramUrl],
      ["pinterest", s.pinterestUrl], ["youtube", s.youtubeUrl], ["linkedin", ""],
    ];
    o["Footer — Social Links"] = pairs.map(([k, v]) => k + ": " + T(v)).join("\n");
  }

  /* ── Main Image ────────────────────────────────────────────────────────────── */
  /* THE FIRST HERO WITH A PHOTO OF ITS OWN, not merely the first hero.
     MEASURED 2026-08-27 on row `rec4Ocv1eC6Zd7394`: Main Image Info came back empty on a
     build whose hero was a real Bynder photo. All seven image blocks are hero-typed, and
     the wordmark art (`ECH_ComingSoonImage`, labelled "Hero Headline Image") sits ABOVE
     the photo in every launch template — so `firstNamed` matched the wordmark, read its
     empty `imgSrc`, and never looked at `ECH_MainImage` below it. Same shape as the bug
     this whole file exists to fix: match the first candidate, stop, report nothing. */
  const heroes = d.filter(isHero);
  const hero = heroes.find((b) => own(b, "imgSrc") !== "") || heroes[0] || null;
  if (hero) {
    const src = own(hero, "imgSrc");
    /* ── A LOCKED PHOTO IS STILL THE PHOTO (2026-08-27, Jack: "if it's locked why
       can't you just send that locked image? and no url") ────────────────────────
       Five of the seven hero blocks — ComingSoonImage, AvailableNowImage,
       JustLaunchedImage, FindDreamHomeImage, VIPImage — declare NO fields at all, so
       `own(hero,"imgSrc")` is "" and this column came back blank on every launch
       template. Katy read that as a missing image and chased it twice.
       Nothing is missing: the art is baked into the block's own markup, one
       `<img class="lnr-hero-img" src="https://lennar.stensul.com/…">` per block
       (verified on all three of ATL's launch banners, 2026-08-27). It is not editable
       and has no click url, which is exactly why it was never in a field — but it IS
       what ships, so it belongs on the row.
       `Main Image Choices` stays "Template Main Image": that is literally what this is,
       and the column is a singleSelect written with typecast off, so a new value like
       "Locked" would 422 the entire 144-field write. */
    const locked = src ? "" : firstImgSrc(dataOf(hero).__html);
    /* ── AN OVERLAY HERO SAYS WHAT IS ON IT (2026-10-09, Jack: "send overlay copy to
       Airtable") ──────────────────────────────────────────────────────────────────
       After ✎ Edit Overlay the field holds the BAKED JPEG, so this column carried a bare
       hosted URL: no words (ops could not copy or proof the headline) and no Bynder link
       (the bake is not an asset). The editor keeps both on the block — `ov*` settings and
       `imgSrc__ovbase` — so the same cell now lists them. Same column, no new ones: Katy's
       Capture reads one cell (contract step 4). services/heroOverlay.ts */
    const ovInfo = src ? overlayInfoLines(dataOf(hero), "imgSrc", (u) => mainImageInfo(u)) : null;
    put("Main Image Info", ovInfo || mainImageInfo(src || locked, heroGifSpecOf(dataOf(hero)["imgSrc__gifsrc"])));
    if (src || locked) o["Main Image Choices"] = "Template Main Image";
    /* ── THE CLICK URL SHIPS TOO (2026-08-31, Katy: "I'm missing the Main Image Link
       URL from the payload") ─────────────────────────────────────────────────────
       ECH_MainImage declares `imgUrl` ("Image link URL", the block's own guardrail) and
       the Studio has always collected it — it just never left the browser: this section
       shipped the photo and dropped the link on the floor. The five locked launch
       banners declare no fields at all, so for them this stays honestly absent, same as
       their photo has no click url in the markup. */
    /* ── THE LINK COMES FROM THE HERO THAT HAS ONE, NOT FROM THE HERO THAT HAS A PHOTO
       (2026-09-11, Katy: "one random test that the Main image link didn't come through
       anywhere in the payload, but in the preview it was linked") ──────────────────
       Reproduced: a Coming Soon whose requester never replaced the placeholder photo.
       `own(photo,"imgSrc")` reads the declared default as "", so `hero` fell to
       `heroes[0]` — the locked wordmark ABOVE it, which has no link field — and the
       real hero's filled `imgUrl` was never read. The rail rendered that same hero with
       its placeholder photo and the community link, hence "linked in the preview".
       (The #230 hard gate now refuses that submit, so the photo half cannot recur; the
       column still has to be right on its own.) Second half of the same repro: the
       fill can resolve the click url to exactly the declared default
       (`https://www.lennar.com/`) — that IS the href the email ships, so the link
       column takes the raw non-empty value rather than `own`, which exists to drop
       copy placeholders. Absent only when no hero carries a link at all (the five
       locked launch banners declare none). */
    const linkOf = (b: EchAware) => T(dataOf(b).imgUrl);
    const linkHero = heroes.find((b) => linkOf(b) !== "") || null;
    put("Main Image Link URL", linkHero ? linkOf(linkHero) : "");
  }

  /* ── Featured plan / community cards ───────────────────────────────────────── */
  const feats = d.filter(echIsFeatured);
  if (feats.length) {
    put("Featured Plan/Community Module", feats.map((b, i) => featuredCard(b, i + 1)).join("\n"));
    /* ── AND ITS IMAGES, ONE LINE EACH, UNCAPPED (2026-08-28, Katy) ─────────────
       Pairs positionally with the list above the same way `Amenities Images` pairs
       with `Amenities`: card N's photo is line N here. The named
       `Featured Community Image 1..3` slots stay as they are — this is the lane that
       does not lose plans 4 and 5 on a five-plan email, which is the case Katy
       named. Each line is "<plan name> — <url>" so a mapper never has to infer
       which card a URL belongs to from its position in a shifting block order. */
    put("Featured Community Images", feats
      .map((b) => imageList(b, () => T(dataOf(b).PlanName) || T(dataOf(b).CommunityName)))
      .filter(Boolean).join("\n"));
  }

  return o;
}
